function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { each } from 'bluebird';
import PropTypes from 'prop-types';
import * as ramda from 'ramda';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Popover } from 'reactstrap';
import { v4 as uuid } from 'uuid';
import classnames from 'classnames';
import { kebabCase } from 'lodash';
import { keysSortedByValueAscending } from '@truqc/content-path';
import Label from '../label';
import { conformanceSelector, enabledSelector, labelSelector, newPathSelector, optionsSelector } from '../../selectors/index';
import entityComposition from '../../../common/classes/entityComposition';
import { evaluateSource } from '../../../../../behaviors/src/lib/evaluateSource';
import { configBehaviorsFunc, ensureConfigBehaviorsReady } from '../../../../nativeBehaviors/configBehaviors';
import { nativeBehaviorEvaluator } from '../../../../nativeBehaviors/nativeBehaviorEvaluator';
import store from '../../../../reducers/storeRegistration';
import ConformanceTooltip from '../conformanceTooltip';
import OptionActionsButtonBody from './optionActionsButtonBody';
import OptionActionsButtonHeader from './optionActionsButtonHeader';
import { getContent } from '../../selectors/simpleSelectors';
const getTransientContext = (data, filterText) => {
  const transientContext = {};
  if (data) {
    transientContext.selection = _objectSpread({}, data);
    // set transient context for value and path
    transientContext.value = data;
  }

  // searching
  transientContext.filterText = filterText;
  return transientContext;
};
const filterOption = option => {
  let newOption;
  if (option !== null && option !== undefined && typeof option === 'object' && Object.keys(option).length > 0) {
    newOption = _objectSpread({}, option);
    // remove the cruft we added on the option
    /* eslint-disable no-underscore-dangle */
    delete newOption._entity;
    delete newOption.identifier;
    delete newOption._isSelected;
    delete newOption._crumbs;
    /* eslint-enable no-underscore-dangle */

    // convert the object-style options to an array
    Object.keys(option).forEach(key => {
      if (typeof option[key] === 'object' && option[key] !== null) {
        newOption[key] = filterOption(option[key]);
      }
    });
  }
  return newOption;
};
const transformDeferredOptions = options => {
  if (Array.isArray(options)) {
    return options;
  }
  const optionKeys = options ? keysSortedByValueAscending(options) : [];
  // convert the object-style options to an array
  return optionKeys.map(id => {
    const option = options[id];
    return _objectSpread(_objectSpread({}, option), {}, {
      identifier: id
    });
  });
};
const OptionActionsButton = ({
  active,
  allowsMultipleSelection: _allowsMultipleSelection = false,
  allowsOther: _allowsOther = false,
  allowsParentSelection: _allowsParentSelection = false,
  conformanceText: _conformanceText = null,
  contentPath: _contentPath = null,
  deferredOptions: _deferredOptions = null,
  enabled,
  label: _label = null,
  options: _options = [],
  path,
  postActions: actions = [],
  reduxEntity,
  title,
  variables: _variables = {}
}) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [buttonID] = useState(() => `oab-${uuid()}`);
  const [wrapperId] = useState(() => `t-oab-${uuid()}`);
  const [selected, setSelected] = useState([]);
  const [initialLoadingDeferredOptions, setInitialLoadingDeferredOptions] = useState(false);
  const [loadingDeferredOptions, setLoadingDeferredOptions] = useState(false);
  const [evaluatedOptions, setEvaluatedOptions] = useState([]);
  const [evaluatedTitle, setEvaluatedTitle] = useState(null);
  const [selectionTree, setSelectionTree] = useState({});
  const [filterText, setFilterText] = useState(null);
  const nativeFunctions = useMemo(() => {
    const evaluator = nativeBehaviorEvaluator.bind(null, active, reduxEntity);
    return {
      errorHandler: err => {
        /* eslint-disable-next-line no-console */
        console.log('error', err);
        throw err;
      },
      evaluator,
      configBehaviors: configBehaviorsFunc
    };
  }, [active, reduxEntity]);

  // when the popover is closed and selection(s) are made, fire the component's `postActions`
  const handleActions = useCallback(async tree => {
    const transientContext = getTransientContext(tree);
    transientContext.path = path;
    const option = filterOption(transientContext);
    await ensureConfigBehaviorsReady();
    // evaluate postActions
    await each(actions, async action => {
      // ensure we're using the latest content from the redux store
      const {
        active: {
          content: updatedContent
        } = {}
      } = store.getState()[reduxEntity];
      let parentPath = path;
      if (_contentPath !== null && _contentPath.length > 0) {
        parentPath = parentPath.substring(0, parentPath.length - _contentPath.length - 1);
      }
      await evaluateSource(action, updatedContent, parentPath, _objectSpread(_objectSpread({}, option), _variables), nativeFunctions);
    });
  }, [actions, nativeFunctions, _variables, path, reduxEntity, _contentPath]);

  // evaluate the `deferredOptions` behavior(s) and transform the results for usable options
  const evaluateDeferredOptions = useCallback(async (newSelection, incomingFilterText = null) => {
    setLoadingDeferredOptions(true);
    let tree;
    if (newSelection) {
      tree = {
        [newSelection.identifier]: _objectSpread({}, newSelection)
      };
    }
    const transientContext = getTransientContext(tree, incomingFilterText);

    // ensure we're using the latest content from the redux store
    const {
      active: {
        content: updatedContent
      } = {}
    } = store.getState()[reduxEntity];
    await ensureConfigBehaviorsReady();
    const newEvaluatedOptions = await evaluateSource(_deferredOptions, updatedContent, path, _objectSpread(_objectSpread({}, transientContext), _variables), nativeFunctions);
    const newOptions = transformDeferredOptions(newEvaluatedOptions.options);
    setEvaluatedOptions(newOptions);
    setEvaluatedTitle(newEvaluatedOptions.title || ' ');
    setLoadingDeferredOptions(false);
  }, [_deferredOptions, nativeFunctions, path, _variables, reduxEntity]);

  // set new state when user clicks back ("<") in the PopoverHeader
  const onBackSelect = useCallback(async () => {
    const updatedSelected = ramda.slice(0, -1, selected);
    setSelected(updatedSelected);
    if (_deferredOptions) {
      let selection;
      if (updatedSelected.length > 0) {
        selection = updatedSelected[updatedSelected.length - 1];
      }
      await evaluateDeferredOptions(selection);
    }
    setFilterText(null);
  }, [_deferredOptions, evaluateDeferredOptions, selected]);

  // control Popover visibility status and take necessary actions on close.
  const toggle = useCallback(async singleSelectValue => {
    setPopoverOpen(!popoverOpen);
    if (popoverOpen) {
      const isMouseEvent = singleSelectValue instanceof MouseEvent;
      if (_allowsMultipleSelection && Object.keys(selectionTree).length > 0) {
        // multiple options have been selected, handleActions
        await handleActions(selectionTree);
      } else if (!_allowsMultipleSelection
      // when popover is not explicitly passed an argument, we get a MouseEvent. Ignore it.
      && !isMouseEvent && singleSelectValue && Object.keys(singleSelectValue).length === 1) {
        // we have made a single selectionTree, handleActions
        await handleActions(singleSelectValue);
      }
      // cleanup on popover close
      setSelected([]);
      setSelectionTree({});
      setEvaluatedOptions([]);
      setEvaluatedTitle(null);
      setFilterText(null);
      setInitialLoadingDeferredOptions(false);
      setLoadingDeferredOptions(false);
    }
  }, [_allowsMultipleSelection, handleActions, popoverOpen, selectionTree]);
  const handleSearch = useCallback(async incomingValue => {
    const value = incomingValue === '' ? null : incomingValue;
    if (!(value === null && filterText === null)) {
      if (_deferredOptions) {
        await evaluateDeferredOptions(selected[selected.length - 1], value);
      }
      setFilterText(value);
    }
  }, [_deferredOptions, evaluateDeferredOptions, filterText, selected]);
  useEffect(() => {
    if (_deferredOptions && popoverOpen && !initialLoadingDeferredOptions && !loadingDeferredOptions) {
      // we should arrive here when the popover first opens to trigger evaluateDeferredOptions
      setInitialLoadingDeferredOptions(true);
      evaluateDeferredOptions();
    }
  }, [_deferredOptions, evaluateDeferredOptions, initialLoadingDeferredOptions, loadingDeferredOptions, popoverOpen]);
  const classNames = useMemo(() => classnames('tqc-control', `t-oab-${kebabCase(title)}`), [title]);
  const wrapperClassNames = classnames('t-oab', {
    'is-invalid': !!_conformanceText
  });
  return /*#__PURE__*/React.createElement("div", {
    id: wrapperId,
    className: wrapperClassNames
  }, _label !== null ? /*#__PURE__*/React.createElement(Label, {
    value: _label
  }) : undefined, /*#__PURE__*/React.createElement(Button, {
    id: buttonID,
    block: true,
    disabled: !enabled,
    type: "button",
    className: classNames
  }, title), /*#__PURE__*/React.createElement(ConformanceTooltip, {
    errorText: _conformanceText,
    target: wrapperId,
    placement: "right"
  }), /*#__PURE__*/React.createElement(Popover, {
    placement: "bottom",
    isOpen: popoverOpen,
    style: {
      width: '250px'
    },
    target: buttonID,
    toggle: toggle,
    trigger: "legacy"
  }, /*#__PURE__*/React.createElement(OptionActionsButtonHeader, {
    evaluateDeferredOptions: evaluateDeferredOptions,
    evaluatedTitle: evaluatedTitle,
    filterText: filterText,
    handleSearch: handleSearch,
    onBackSelect: onBackSelect,
    selected: selected
  }), /*#__PURE__*/React.createElement(OptionActionsButtonBody, {
    allowsMultipleSelection: _allowsMultipleSelection,
    allowsParentSelection: _allowsParentSelection,
    allowsOther: _allowsOther,
    deferredOptions: _deferredOptions,
    evaluatedOptions: evaluatedOptions,
    evaluateDeferredOptions: evaluateDeferredOptions,
    filterText: filterText,
    handleSearch: handleSearch,
    loadingDeferredOptions: loadingDeferredOptions,
    options: _options,
    selected: selected,
    selectionTree: selectionTree,
    setFilterText: setFilterText,
    setSelected: setSelected,
    setSelectionTree: setSelectionTree,
    toggle: toggle
  })));
};
OptionActionsButton.propTypes = {
  allowsOther: PropTypes.bool,
  variables: PropTypes.shape({}),
  allowsMultipleSelection: PropTypes.bool,
  allowsParentSelection: PropTypes.bool,
  active: PropTypes.shape({}).isRequired,
  conformanceText: PropTypes.string,
  deferredOptions: PropTypes.shape({}),
  enabled: PropTypes.bool.isRequired,
  label: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({})),
  path: PropTypes.string.isRequired,
  postActions: PropTypes.arrayOf(PropTypes.shape({})),
  reduxEntity: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  contentPath: PropTypes.string
};
export { OptionActionsButton as UncomposedOptionActionButton };
export default entityComposition(OptionActionsButton, [], {
  conformanceText: conformanceSelector,
  enabled: enabledSelector,
  label: labelSelector,
  options: optionsSelector,
  path: newPathSelector,
  content: getContent,
  active: (state, {
    reduxEntity
  }) => state[reduxEntity].active
}, []);